import { useMemo } from 'react'

import type { EChartsOption, LineSeriesOption } from 'echarts'

import { type BarRowItem, buildBarRowSeries as buildSharedBarRowSeries } from '@/components/charts/history/barRow'
import { buildMidnightMarkLineSeries, makeTimeAxis as makeSharedTimeAxis } from '@/components/charts/history/timeAxis'
import { convertCelsius, tempUnitSuffix } from '@/utils/conversions'
import { formatDate, formatDurationLong, formatHistoryDateTime, standardDateTimeFormat } from '@/utils/formatDate'

import { buildAlertMarkerSeries } from './alertMarkerSeries'
import {
  CIT_OVERLAY_OPACITY,
  GRID_SHARED,
  MODE_GAP_MS,
  STATUS_AXIS_PX,
  STATUS_BELOW_BAR_PX,
  STATUS_BELOW_PAD_PX,
  STATUS_GRID_GAP,
  STATUS_LABEL_LEFT,
  STATUS_ROW_PX,
  TEMP_LIMIT_LOWER_COLOR,
  TEMP_LIMIT_LOWER_SERIES,
  TEMP_LIMIT_UPPER_COLOR,
  TEMP_LIMIT_UPPER_SERIES,
} from './constants'
import { addressLookup } from './cursorInfo'
import { defaultPalette, resolveColor, resolveLabel } from './palette'
import { type StatusLayout, buildStatusLayout } from './statusRows'
import { buildGeofenceRowSeries, buildReeferDetailSeries, buildStatusRowsAxis } from './statusRowSeries'
import { buildGeofenceMarkAreas } from './transform'
import { capitalize, normalizeSeverity } from './transform/alertsAlarms'
import type { TemperatureResult } from './transform/temperature'
import type {
  AlertMarker,
  AssetHistory,
  CITState,
  DoorEventSample,
  LaneInterval,
  TempUnits,
  TemperatureLimits,
  ZoomRange,
} from './types'
import { yAxisLayout } from './yAxisLayout'

// Legend/series name for the alert pin markers. Toggling this in the side panel
// will show/hide all alert pins in the chart.
export const ALERT_PINS_SERIES = '__alert_pins__'

/** Legend/series name for door openings when geofences are hidden (one series, no inside/outside split). */
export const DOOR_OPEN_SERIES = '__door_open__'

/** Line colour, marker icon and display label per alert severity code, keyed the
 *  same way the acknowledgement page marker is (1 informational, 2 warning,
 *  3 critical). Exported so the legend swatch and its colour key match the lines
 *  drawn in the chart. */
export const ALERT_PIN_STYLES: Record<number, { line: string; icon: string; label: string }> = {
  1: { line: '#22c55e', icon: '/images/alert-green-triangle.svg', label: 'Informational' },
  2: { line: '#eab308', icon: '/images/alert-yellow-triangle.svg', label: 'Warning' },
  3: { line: '#ef4444', icon: '/images/alert-red-triangle.svg', label: 'Critical' },
}

//  Mode legend: the full key is shown in the legend's hover popover.
export const ALERT_SEVERITY_KEY = [1, 2, 3].map((code) => ({ code, ...ALERT_PIN_STYLES[code] }))

/** CIT overlay data passed from HistoryLanes */
export interface CITOverlayEntry {
  key: string
  label: string
  colorMapKey: string
  intervals: LaneInterval[]
}

/** Where a status lane's bars are drawn: its lane Y in the temperature grid (grid 0) or its
 *  category row in the grid under the graph (grid 1). */
export interface StatusPlacement {
  xAxisIndex: number
  yAxisIndex: number
  anchorY: number
  barHeight: number
}

/** Scan temperature series to find y-axis data range. Series the user has hidden
 *  via the legend are skipped so the axis rescales to only the visible values. */
function computeYRange(tempSeries: LineSeriesOption[], selected: Record<string, boolean>): [number, number] | null {
  let yMin = Infinity
  let yMax = -Infinity
  for (const s of tempSeries) {
    if (selected[s.name as string] === false) continue // hidden series
    const d = s.data as [string, number][]
    if (!d?.length) continue
    for (const [, v] of d) {
      if (v < yMin) yMin = v
      if (v > yMax) yMax = v
    }
  }
  if (yMin === Infinity) return null
  return [yMin, yMax]
}

/** Extract door-open intervals as [start, end] epoch ms pairs */
interface DoorOpenInterval {
  start: number
  end: number
  /** True if there's no closing event after this open — the door is still open
   *  as far as we know. `end` is set to "now" for rendering purposes only. */
  isStillOpen: boolean
}

function buildDoorOpenIntervals(events: DoorEventSample[]): DoorOpenInterval[] {
  if (!events?.length) return []
  const intervals: DoorOpenInterval[] = []
  for (let i = 0; i < events.length; i++) {
    if (events[i].status !== 'open') continue
    const start = new Date(events[i].time).getTime()
    if (i < events.length - 1) {
      intervals.push({ start, end: new Date(events[i + 1].time).getTime(), isStillOpen: false })
    } else {
      intervals.push({ start, end: Date.now(), isStillOpen: true })
    }
  }
  return intervals
}

export function useChartOptions(
  data: AssetHistory | undefined,
  tempResult: TemperatureResult | undefined,
  legendSelected: Record<string, boolean>,
  citState: CITState,
  citOverlays: CITOverlayEntry[],
  periodWindow: ZoomRange,
  units: TempUnits,
  zone: string,
  alertMarkers?: AlertMarker,
  temperatureLimits?: TemperatureLimits,
  showTabs = false,
  hideGeofences = false,
  statusLayout?: StatusLayout
): EChartsOption {
  return useMemo(() => {
    if (!data || !tempResult) return {}

    // Render all tooltip timestamps in the user's profile zone, with the tz
    // abbreviation appended.
    const fmtTime = (v: string | Date | number): string => formatHistoryDateTime(v, zone)

    const palette = defaultPalette
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const allSeries: any[] = []

    // --- Temperature series (single grid) ---
    const temp = tempResult

    // Resolve which series are currently visible. Linked sub-series follow their
    // parent's visibility. Computed up front so the y-axis range can ignore any
    // series the user has hidden and rescale to just the visible values.
    const mergedSelected: Record<string, boolean> = { ...legendSelected }
    for (const link of temp.legendLinked) {
      mergedSelected[link.name] = mergedSelected[link.linkedTo] ?? true
    }

    // --- Temperature limits ---
    // Resolved up here because both the series (drawn well above) and the y-axis range
    // (computed well below) need them. Values arrive in Celsius and convert like every
    // other temperature on this chart. A limit the user has toggled off resolves to
    // undefined, so it stops stretching the axis as well as leaving the plot.
    const limitLine = (value: number | null | undefined, seriesName: string): number | undefined => {
      if (value == null || mergedSelected[seriesName] === false) return undefined
      return Number(convertCelsius(value, units).toFixed(1))
    }
    const upperLimit = limitLine(temperatureLimits?.upper, TEMP_LIMIT_UPPER_SERIES)
    const lowerLimit = limitLine(temperatureLimits?.lower, TEMP_LIMIT_LOWER_SERIES)
    const activeLimits = [upperLimit, lowerLimit].filter((v): v is number => v != null)

    // Nearest address (within 15 minutes) for the tooltips.
    const findAddress = addressLookup(data.location ?? [])

    // Add tooltip with temperature, date/time, and address to each temperature series
    for (const s of temp.series) {
      const name = s.name as string
      if (name.startsWith('__')) {
        continue // skip internal series
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((s as any).tooltip) {
        continue // preserve series-specific tooltips (e.g. VSP shows period start/end)
      }
      const isSetpoint = name.includes('SetPoint')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(s as any).tooltip = {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formatter: (params: any) => {
          const [time, value] = params.data as [string | number, number]
          const lines = [
            `<b>${params.seriesName}</b>: ${typeof value === 'number' ? value.toFixed(isSetpoint ? 0 : 1) : value}${tempUnitSuffix(units)}`,
            fmtTime(time),
          ]
          const addr = findAddress(time)
          if (addr) lines.push(addr)
          return lines.join('<br/>')
        },
      }
    }

    // Geofence markAreas on a dedicated invisible line. A fixed site (cold storage) has
    // no use for geofences, so hideGeofences drops them along with the door split below.
    // No transparent outside-geofence bands: the cursor popover says where the asset was.
    const geofenceResult = buildGeofenceMarkAreas(hideGeofences ? [] : data.location, { outsideBands: false })
    if (geofenceResult.markArea.data?.length) {
      allSeries.push({
        type: 'line',
        name: '__geofence__',
        xAxisIndex: 0,
        yAxisIndex: 0,
        data: [],
        silent: false,
        symbol: 'none',
        lineStyle: { width: 0 },
        markArea: geofenceResult.markArea,
        z: 0,
      })
    }

    const degrees = tempUnitSuffix(units)
    const pushLimitSeries = (name: string, value: number, color: string, label: string) => {
      allSeries.push({
        type: 'line',
        name,
        xAxisIndex: 0,
        yAxisIndex: 0,
        data: [],
        symbol: 'none',
        lineStyle: { width: 0 },
        silent: true,
        markLine: {
          silent: true,
          symbol: 'none',
          animation: false,
          data: [
            {
              yAxis: value,
              lineStyle: { color, type: 'solid', width: 1.5 },
              label: {
                show: true,
                position: 'insideEndTop',
                formatter: `${label} ${value}${degrees}`,
                fontSize: 9,
                color,
              },
            },
          ],
        },
        z: 1,
      })
    }
    if (upperLimit != null) {
      pushLimitSeries(TEMP_LIMIT_UPPER_SERIES, upperLimit, TEMP_LIMIT_UPPER_COLOR, 'Upper')
    }
    if (lowerLimit != null) {
      pushLimitSeries(TEMP_LIMIT_LOWER_SERIES, lowerLimit, TEMP_LIMIT_LOWER_COLOR, 'Lower')
    }

    // Door-open hatched overlay — split into two series so inside/outside can be
    // toggled independently from the legend.
    // Intervals are clipped to the visible window because the API may return a
    // door-open event from before the window (so we know the door was already
    // open at window start). Without clipping, renderItem's hatch loop iterates
    // across the full off-screen pixel width and hangs the canvas.
    const doorOpenIntervals = buildDoorOpenIntervals(data.doorEvents)
    const doorColorOutside = '#b91c1c' // red-700 for outside geofence (darker for visibility over data)
    const doorColorInside = '#1d4ed8' // blue-700 for inside geofence (darker for visibility over data)
    type DoorBucket = {
      visStart: number
      visEnd: number
      origStart: number
      origEnd: number
      isStillOpen: boolean
    }
    const insideDoors: DoorBucket[] = []
    const outsideDoors: DoorBucket[] = []
    // Every door when geofences are hidden: no inside/outside split.
    const plainDoors: DoorBucket[] = []
    if (doorOpenIntervals.length) {
      const gfIntervals = geofenceResult.intervals
      const winStart = periodWindow[0]
      const winEnd = periodWindow[1]
      for (const { start: dStart, end: dEnd, isStillOpen } of doorOpenIntervals) {
        const visStart = Math.max(dStart, winStart)
        const visEnd = Math.min(dEnd, winEnd)
        if (visStart >= visEnd) continue // entirely outside window
        const inside = gfIntervals.some((gf) => gf.inside && gf.start < dEnd && gf.end > dStart)
        const bucket = hideGeofences ? plainDoors : inside ? insideDoors : outsideDoors
        bucket.push({ visStart, visEnd, origStart: dStart, origEnd: dEnd, isStillOpen })
      }
    }

    // --- Status-bar layout ---------------------------------------------------
    // The layout decides which lanes sit in the temperature grid (grid 0, at the data-
    // coordinate lane Ys computed below) and which rows go in the grid under the graph
    // (grid 1, one category row each, with collapsible section headers). Doors are exempt
    // either way: always full-height hash overlays on the temperature grid.
    const layout = statusLayout ?? buildStatusLayout(data, false, new Set(), hideGeofences)
    const statusLanes = layout.inGraph
    const belowRows = layout.below
    const hasAnyLane = statusLanes.length > 0
    const hasBelow = belowRows.length > 0
    const rowIndex = new Map(belowRows.map((row, i) => [row.key, i]))

    // One zone-mode lane per zone that has mode samples (all zones on the All tab,
    // just the active zone on a single-zone tab). Order follows data.zones.
    const zoneModeLanes = Object.entries(data.zones).filter(([, z]) => z && (z.mode?.length ?? 0) > 0) as [
      string,
      NonNullable<AssetHistory['zones'][string]>,
    ][]
    const alarmLanes = (data.alarms ?? []).filter((a) => a.times.length > 0)

    const activeCITKeys = citOverlays.filter((o) => citState[o.key]).map((o) => o.key)

    /** `inside` null: geofences hidden, so the tooltip says nothing about them. */
    function buildDoorSeries(name: string, buckets: DoorBucket[], color: string, inside: boolean | null) {
      return {
        type: 'custom',
        name,
        xAxisIndex: 0,
        yAxisIndex: 0,
        clip: true,
        silent: false,
        // value carries only the visible range (renderItem reads it). Original
        // event times ride alongside as extra props so ECharts' dimension
        // inference doesn't choke on a 4-wide value array.
        data: buckets.map((b) => ({
          value: [b.visStart, b.visEnd],
          _origStart: b.origStart,
          _origEnd: b.origEnd,
          _isStillOpen: b.isStillOpen,
        })),
        renderItem: (
          params: { coordSys: { y: number; height: number } },
          api: {
            coord: (v: [number, number]) => [number, number]
            value: (idx: number) => number
          }
        ) => {
          const { y: gridY, height: gridH } = params.coordSys
          const x1 = api.coord([api.value(0), 0])[0]
          const x2 = api.coord([api.value(1), 0])[0]
          const w = Math.max(x2 - x1, 1)
          const lineSpacing = 6

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const children: any[] = [
            {
              type: 'rect',
              shape: { x: x1, y: gridY, width: w, height: gridH },
              style: { fill: 'transparent' },
            },
          ]

          for (let offset = -gridH; offset < w + gridH; offset += lineSpacing) {
            const sx = x1 + offset
            const ex = sx + gridH
            children.push({
              type: 'line',
              shape: {
                x1: Math.max(x1, sx),
                y1: gridY + Math.max(0, Math.max(x1, sx) - sx),
                x2: Math.min(x1 + w, ex),
                y2: gridY + Math.min(gridH, Math.min(x1 + w, ex) - sx),
              },
              style: { stroke: color, lineWidth: 1, opacity: 0.45 },
            })
          }

          return { type: 'group', children }
        },
        tooltip: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          formatter: (params: any) => {
            const d = params.data
            const origStart = d?._origStart
            const origEnd = d?._origEnd
            const isStillOpen: boolean = !!d?._isStillOpen
            if (origStart == null || origEnd == null) return ''
            const lines = [`<b>Door Open</b>`, `Started: ${fmtTime(origStart)}`]
            if (isStillOpen) {
              lines.push('Still open')
              lines.push(`Duration: ${formatDurationLong(origStart, Date.now())}`)
            } else {
              lines.push(`Ended: ${fmtTime(origEnd)}`)
              lines.push(`Duration: ${formatDurationLong(origStart, origEnd)}`)
            }
            const addr = findAddress(origStart)
            if (addr) lines.push(addr)
            if (inside != null) lines.push(inside ? `In Geofence` : `Out of Geofence`)
            return lines.join('<br/>')
          },
        },
        encode: { x: [0, 1] },
        z: 2,
      }
    }

    if (insideDoors.length) {
      allSeries.push(buildDoorSeries('__door_open_inside__', insideDoors, doorColorInside, true))
    }
    if (outsideDoors.length) {
      allSeries.push(buildDoorSeries('__door_open_outside__', outsideDoors, doorColorOutside, false))
    }
    if (plainDoors.length) {
      // Geofences are hidden only for cold storage, which is always inside one: draw doors blue.
      allSeries.push(buildDoorSeries(DOOR_OPEN_SERIES, plainDoors, doorColorInside, null))
    }

    // Midnight markLines (vertical day-divider lines). Date labels live on the
    // axis itself so every midnight tick renders identically — including the
    // leftmost tick when the window starts at midnight.
    const midnightSeries = buildMidnightMarkLineSeries(periodWindow, zone)
    if (midnightSeries) allSeries.push(midnightSeries)

    allSeries.push(...temp.series)

    const alertMarkerSeries = buildAlertMarkerSeries(alertMarkers, periodWindow, zone)
    if (alertMarkerSeries) allSeries.push(alertMarkerSeries)

    // Alert pins — one vertical marker per subscribed alert raised inside the window.
    const alertPins = (data.alertPins ?? []).filter((pin) => {
      const t = new Date(pin.raisedTime).getTime()
      return Number.isFinite(t) && t >= periodWindow[0] && t <= periodWindow[1]
    })

    if (alertPins.length) {
      const pinTooltips = new Map<number, string>()
      for (const pin of alertPins) {
        const raisedMs = new Date(pin.raisedTime).getTime()
        const text = `<b>${pin.alertName}</b><br/>${formatDate(pin.raisedTime, standardDateTimeFormat, zone)}`
        const existing = pinTooltips.get(raisedMs)
        pinTooltips.set(raisedMs, existing ? `${existing}<br/><br/>${text}` : text)
      }

      allSeries.push({
        type: 'line',
        name: ALERT_PINS_SERIES,
        xAxisIndex: 0,
        yAxisIndex: 0,
        data: [],
        symbol: 'none',
        lineStyle: { width: 0 },
        markLine: {
          symbol: ['none', 'none'],
          symbolSize: [0, 0],
          tooltip: {
            trigger: 'item',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formatter: (params: any) => pinTooltips.get(Number(params?.value ?? params?.data?.xAxis)) ?? '',
          },
          data: alertPins.map((pin) => {
            const raisedMs = new Date(pin.raisedTime).getTime()
            const style = ALERT_PIN_STYLES[pin.severityCode] ?? ALERT_PIN_STYLES[3]
            return {
              xAxis: raisedMs,
              name: pin.alertName,
              lineStyle: { color: style.line, type: 'solid', width: 2 },
              label: {
                show: true,
                formatter: '{icon|}',
                rich: {
                  icon: {
                    backgroundColor: { image: style.icon },
                    width: 18,
                    height: 18,
                    align: 'center' as const,
                  },
                },
                position: 'end' as const,
                distance: 4,
              },
            }
          }),
        },
      })
    }

    // Both marker kinds label above the plot top, so either one needs the same
    const hasTopMarkers = Boolean(alertMarkers?.raisedTime) || alertPins.length > 0

    // --- Y-axis range + status-lane Ys ---
    const numActiveCIT = activeCITKeys.length

    // The temperature band covers the plotted temperatures and the threshold lines, with the
    // status lanes packed beneath it (single grid) and CIT bars above. Lane spacing is a
    // fraction of the band (not a fixed degree count) so the lanes sit at the same pixel
    // position whatever the unit. Lanes are allocated top-down in `statusLanes` order, so an
    // absent lane leaves no gap.
    const yAxis = yAxisLayout({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: computeYRange(temp.series as any, mergedSelected),
      limits: activeLimits,
      laneCount: statusLanes.length,
      citCount: numActiveCIT,
      belowChart: false,
      units,
    })
    const { tempMin, tempMax, span, barHeight: barDataHeight, barGap: barDataGap } = yAxis
    const yAxisMin = yAxis.axisMin
    const yAxisMax = yAxis.axisMax
    const laneYByKey = new Map(statusLanes.map((lane, i) => [lane.key, yAxis.laneY(i)]))

    // Null when the lane isn't drawn at all: no data, or its section under the graph is collapsed.
    const placementFor = (key: string): StatusPlacement | null => {
      const laneY = laneYByKey.get(key)
      if (laneY != null) return { xAxisIndex: 0, yAxisIndex: 0, anchorY: laneY, barHeight: 6 }
      const row = rowIndex.get(key)
      if (row != null) return { xAxisIndex: 1, yAxisIndex: 1, anchorY: row, barHeight: STATUS_BELOW_BAR_PX }
      return null
    }

    // Shared builder for a single-row status bar (rect per [start,end] interval), placed
    // in whichever grid the lane lives; renderItem's api.coord resolves against the axis
    // the series is bound to (data-coordinate lane Y on grid 0, row index on grid 1).
    function buildBarRowSeries(
      placement: StatusPlacement,
      opts: {
        name: string
        data: BarRowItem[]
        colorFor: (dataIndex: number) => string
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        tooltip?: (dataItem: any) => string
        // in-bar label shown when the bar is wide enough
        label?: (dataIndex: number) => { text: string; color: string } | null
        z?: number
      }
    ) {
      return buildSharedBarRowSeries({ ...opts, ...placement })
    }

    const citLegendNames: string[] = []

    for (const overlay of citOverlays) {
      if (!citState[overlay.key]) continue

      const citDisplayName = overlay.label
      citLegendNames.push(citDisplayName)

      const stackIndex = activeCITKeys.indexOf(overlay.key)

      // Position this bar in the padded region at the top of the y-axis
      const barTopY = yAxisMax - span * 0.02 - stackIndex * (barDataHeight + barDataGap)
      const barBotY = barTopY - barDataHeight

      allSeries.push({
        type: 'custom',
        name: citDisplayName,
        xAxisIndex: 0,
        yAxisIndex: 0,
        clip: true,
        data: overlay.intervals.map((iv) => ({
          value: [iv.start, 0, iv.end, iv.value],
        })),
        renderItem: (
          params: { dataIndex: number },
          api: {
            coord: (v: [number, number]) => [number, number]
            value: (idx: number) => number | string
          }
        ) => {
          const startX = api.coord([api.value(0) as number, barTopY])[0]
          const endX = api.coord([api.value(2) as number, barTopY])[0]
          const topPixel = api.coord([0, barTopY])[1]
          const botPixel = api.coord([0, barBotY])[1]
          const height = Math.max(botPixel - topPixel, 2)

          const stateValue = api.value(3) as string
          const color = resolveColor(palette, overlay.colorMapKey, stateValue)
          const displayLabel = resolveLabel(palette, overlay.colorMapKey, stateValue)
          const barWidth = Math.max(endX - startX, 1)

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const children: any[] = [
            {
              type: 'rect',
              shape: { x: startX, y: topPixel, width: barWidth, height },
              style: { fill: color, opacity: CIT_OVERLAY_OPACITY },
            },
          ]

          // Label on first data item: bar name to the left
          if (params.dataIndex === 0) {
            children.push({
              type: 'text',
              x: startX + 3,
              y: topPixel + height / 2,
              style: {
                text: citDisplayName,
                fontSize: 8,
                fill: '#374151',
                textVerticalAlign: 'middle',
              },
            })
          }
          // Value label inside bar when wide enough
          if (barWidth > 40 && params.dataIndex > 0) {
            children.push({
              type: 'text',
              x: startX + 3,
              y: topPixel + height / 2,
              style: {
                text: displayLabel,
                fontSize: 7,
                fill: '#6b7280',
                textVerticalAlign: 'middle',
              },
            })
          }

          return { type: 'group', children }
        },
        tooltip: {
          formatter: (params: { value: (string | number)[] }) => {
            const v = params.value
            const stateValue = v[3] as string
            const displayLabel = resolveLabel(palette, overlay.colorMapKey, stateValue)
            const start = fmtTime(v[0] as number)
            const end = fmtTime(v[2] as number)
            return `<b>${overlay.label}</b>: ${displayLabel}<br/>${start} \u2013 ${end}`
          },
        },
        encode: { x: [0, 2] },
        z: 10 + stackIndex,
      })
    }

    // Power state line along bottom of chart
    const powerSamples = data.powerState ?? []
    const powerPlacement = placementFor('power')
    if (powerSamples.length && powerPlacement) {
      // In the graph: the lane Y below all temperature data. Under it: the row on grid 1.
      const powerAnchor = powerPlacement.anchorY

      // Build runs of consecutive "on" samples. A run is provisionally "stillOn"
      // when the in-window data ended without an off sample; the edge corrections
      // below confirm whether it really is on as of now or just continued past the
      // window (turned off later).
      //
      // start/end are the visible (window-clamped) coords used to draw the line and
      // place the pins. trueStart/trueEnd are the real continuous-on boundaries used
      // for the tooltip + duration, which may fall outside the window (see the edge
      // corrections below) so the duration reflects how long power has really been on.
      const nowMs = Date.now()
      interface PowerRun {
        start: number
        end: number
        trueStart: number
        trueEnd: number
        stillOn: boolean
      }
      const onRuns: PowerRun[] = []
      let runStart: number | null = null
      for (let i = 0; i < powerSamples.length; i++) {
        const time = new Date(powerSamples[i].time).getTime()
        const isOn = powerSamples[i].value === 'O'
        if (isOn && runStart === null) {
          runStart = time
        } else if (!isOn && runStart !== null) {
          onRuns.push({ start: runStart, end: time, trueStart: runStart, trueEnd: time, stillOn: false })
          runStart = null
        }
      }
      // Close final run — power was still on at the last in-window sample.
      if (runStart !== null) {
        const end = Math.min(nowMs, periodWindow[1])
        onRuns.push({ start: runStart, end, trueStart: runStart, trueEnd: end, stillOn: true })
      }

      // Edge corrections from the backend, which looks just outside the window:
      // - priorPowerOn: when the run that was already on at the window start really
      //   began (before the window). Apply only when the first in-window sample is
      //   itself "on", i.e. that run genuinely spans the window start.
      // - nextPowerOff: the first power-off after the window end. If present, the
      //   final open run actually ended there (not "still on"); if absent, power is
      //   still on as of now, so the duration runs to the current time.
      const priorPowerOn = data.priorPowerOn ? new Date(data.priorPowerOn).getTime() : null
      const nextPowerOff = data.nextPowerOff ? new Date(data.nextPowerOff).getTime() : null
      if (onRuns.length && priorPowerOn != null && powerSamples[0]?.value === 'O') {
        onRuns[0].trueStart = priorPowerOn
      }
      const lastRun = onRuns[onRuns.length - 1]
      if (lastRun?.stillOn) {
        if (nextPowerOff != null) {
          lastRun.stillOn = false
          lastRun.trueEnd = nextPowerOff
        } else {
          lastRun.trueEnd = nowMs
        }
      }

      // One rect per run, drawn as a custom series (like Movement) so the whole
      // bar is a hoverable shape — a thin line with no symbols wouldn't trigger the
      // item tooltip on its stroke. Each item carries _trueStart/_trueEnd/_stillOn
      // so the tooltip shows the full run wherever the bar is hovered.
      const POWER_BAR_HEIGHT = powerPlacement.barHeight
      const powerData = onRuns.map((run) => ({
        value: [run.start, run.end],
        _trueStart: run.trueStart,
        _trueEnd: run.trueEnd,
        _stillOn: run.stillOn,
      }))

      // One tooltip for the whole run — identical on the bar and on any pin:
      // Power On / Power Off (a real time, or "Current" while still on) / Duration.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const powerTooltip = (d: any): string => {
        if (!d?._trueStart) return ''
        const off = d._stillOn ? 'Current' : fmtTime(d._trueEnd)
        return [
          `Power On: ${fmtTime(d._trueStart)}`,
          `Power Off: ${off}`,
          `Duration: ${formatDurationLong(d._trueStart, d._trueEnd)}`,
        ].join('<br/>')
      }

      // Pins mark only true transitions:an "Off" pin where it
      // actually went off (omitted when that's after the window).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const powerMarkPoints: any[] = []
      for (const run of onRuns) {
        const marker = (x: number, label: string, color: string, size = 30) => ({
          coord: [x, powerAnchor],
          symbol: 'pin',
          symbolSize: size,
          label: { show: true, formatter: label, fontSize: 8, color: '#fff', lineHeight: 9 },
          itemStyle: { color },
          _trueStart: run.trueStart,
          _trueEnd: run.trueEnd,
          _stillOn: run.stillOn,
        })
        if (run.trueStart >= periodWindow[0]) {
          powerMarkPoints.push(marker(run.start, 'On', '#16a34a'))
        }
        if (!run.stillOn && run.trueEnd <= periodWindow[1]) {
          powerMarkPoints.push(marker(run.end, 'Off', '#ef4444'))
        }
      }

      allSeries.push({
        type: 'custom',
        name: 'Power State',
        xAxisIndex: powerPlacement.xAxisIndex,
        yAxisIndex: powerPlacement.yAxisIndex,
        clip: true,
        silent: false,
        data: powerData,
        renderItem: (
          _params: unknown,
          api: {
            coord: (v: [number, number]) => [number, number]
            value: (idx: number) => number
          }
        ) => {
          const startX = api.coord([api.value(0), powerAnchor])[0]
          const endX = api.coord([api.value(1), powerAnchor])[0]
          const centerY = api.coord([0, powerAnchor])[1]
          const w = Math.max(endX - startX, 1)
          return {
            type: 'rect',
            shape: { x: startX, y: centerY - POWER_BAR_HEIGHT / 2, width: w, height: POWER_BAR_HEIGHT },
            style: { fill: '#16a34a' },
          }
        },
        encode: { x: [0, 1] },
        z: 5,
        markPoint: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          data: powerMarkPoints as any,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          tooltip: { formatter: (params: any) => powerTooltip(params.data) },
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        tooltip: { formatter: (params: any) => powerTooltip(params.data) },
      })
    }

    // Zone mode lane(s) below power state — one bar per zone (all zones on the All
    // tab, the active zone on a single-zone tab). One rect per sample interval
    // [sample i, sample i+1], colored by that sample's mode. We don't compute a
    // duration; like the old app the tooltip just reports the sample under the
    // cursor: zone, mode, time, and nearest address. A gap larger than MODE_GAP_MS
    // means the unit was off/silent between readings, so that segment collapses to a
    // tick and the bar shows a hole instead of implying continuous operation.
    const zoneModeVisEnd = Math.min(Date.now(), periodWindow[1])
    interface ModeSeg {
      start: number
      end: number
      value: string
    }
    for (const [zoneName, zone] of zoneModeLanes) {
      const samples = zone.mode
      const placement = placementFor(`zoneMode:${zoneName}`)
      if (!placement) continue
      const laneAnchor = placement.anchorY
      const barHeight = placement.barHeight
      const modeSegs: ModeSeg[] = samples.map((s, i) => {
        const start = new Date(s.time).getTime()
        const nextT = i < samples.length - 1 ? new Date(samples[i + 1].time).getTime() : zoneModeVisEnd
        const end = nextT - start > MODE_GAP_MS ? start : nextT
        return { start, end, value: s.value }
      })

      // value stays purely numeric [start, end]; mixing the mode string in makes
      // ECharts coerce that dimension so api.value() returns garbage and the color
      // falls back to gray. renderItem reads the mode from modeSegs by dataIndex.
      const seriesData = modeSegs.map((seg) => ({
        value: [seg.start, seg.end],
        _value: seg.value,
        _time: seg.start,
      }))

      // Tooltip: zone + mode of the hovered sample, its time, and nearest address.
      // (Zone is included because every zone's bar uses the same mode palette.)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const zoneModeTooltip = (d: any): string => {
        const v = d?._value as string | undefined
        if (!v) return ''
        const lines = [`<b>${zoneName} Mode</b>: ${resolveLabel(palette, 'zoneMode', v)}`, fmtTime(d._time)]
        const addr = findAddress(d._time)
        if (addr) lines.push(addr)
        return lines.join('<br/>')
      }

      allSeries.push({
        type: 'custom',
        name: `${zoneName} Mode`,
        xAxisIndex: placement.xAxisIndex,
        yAxisIndex: placement.yAxisIndex,
        clip: true,
        silent: false,
        data: seriesData,
        renderItem: (
          params: { dataIndex: number },
          api: {
            coord: (v: [number, number]) => [number, number]
            value: (idx: number) => number | string
          }
        ) => {
          const startX = api.coord([api.value(0) as number, laneAnchor])[0]
          const endX = api.coord([api.value(1) as number, laneAnchor])[0]
          const centerY = api.coord([0, laneAnchor])[1]
          const w = Math.max(endX - startX, 1)
          const color = resolveColor(palette, 'zoneMode', modeSegs[params.dataIndex].value)
          return {
            type: 'rect',
            shape: { x: startX, y: centerY - barHeight / 2, width: w, height: barHeight },
            style: { fill: color },
          }
        },
        encode: { x: [0, 1] },
        z: 5,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        tooltip: { formatter: (params: any) => zoneModeTooltip(params.data) },
      })
    }

    const alarmVisEnd = Math.min(Date.now(), periodWindow[1])
    alarmLanes.forEach((alarm, i) => {
      const placement = placementFor(`alarm:${i}`)
      if (!placement) return
      const severity = normalizeSeverity(alarm.severity)
      const color = palette.alarms[severity] ?? '#9ca3af'
      const label = alarm.description || `Alarm ${i + 1}`
      const code = alarm.code

      const laneData: BarRowItem[] = alarm.times.map((t) => {
        const start = new Date(t.raisedTime).getTime()
        const active = !t.clearedTime
        const end = active ? alarmVisEnd : new Date(t.clearedTime!).getTime()
        return { value: [start, end], _start: start, _end: end, _active: active }
      })

      allSeries.push(
        buildBarRowSeries(placement, {
          name: label,
          data: laneData,
          colorFor: () => color,
          tooltip: (d: BarRowItem) =>
            [
              `<b>${code}:${label}</b> — ${capitalize(severity)}`,
              `Raised: ${fmtTime(d._start)}`,
              d._active
                ? 'Cleared: Active'
                : `Cleared:
  ${fmtTime(d._end)}`,
            ].join('<br/>'),
        })
      )
    })

    // Movement bars below power state
    const movementSamples = data.movement ?? []
    const movementPlacement = placementFor('movement')
    if (movementSamples.length && movementPlacement) {
      const MOVEMENT_COLOR = '#000000' // gray-500
      const BAR_HEIGHT = movementPlacement.barHeight
      const movementAnchor = movementPlacement.anchorY

      allSeries.push({
        type: 'custom',
        name: 'Movement',
        xAxisIndex: movementPlacement.xAxisIndex,
        yAxisIndex: movementPlacement.yAxisIndex,
        clip: true,
        silent: false,
        // Samples missing stopTime are in-progress trips — extend to "now"
        // (clipped to the window so a sample doesn't paint past the visible area)
        // and flag stillMoving so the tooltip says so instead of inventing a stop time.
        data: movementSamples.map((m) => {
          const start = new Date(m.startTime).getTime()
          const stillMoving = !m.stopTime
          const end = stillMoving ? Math.min(Date.now(), periodWindow[1]) : new Date(m.stopTime!).getTime()
          return { value: [start, end, stillMoving ? 1 : 0] }
        }),
        renderItem: (
          _params: unknown,
          api: {
            coord: (v: [number, number]) => [number, number]
            value: (idx: number) => number
          }
        ) => {
          const startX = api.coord([api.value(0), movementAnchor])[0]
          const endX = api.coord([api.value(1), movementAnchor])[0]
          const centerY = api.coord([0, movementAnchor])[1]
          const w = Math.max(endX - startX, 1)
          return {
            type: 'rect',
            shape: { x: startX, y: centerY - BAR_HEIGHT / 2, width: w, height: BAR_HEIGHT },
            style: { fill: MOVEMENT_COLOR },
          }
        },
        tooltip: {
          formatter: (params: { value: number[] }) => {
            const start = params.value[0]
            const end = params.value[1]
            const stillMoving = params.value[2] === 1
            const startAddr = findAddress(start)
            const endAddr = findAddress(end)
            const lines = [`<b>Movement</b>`, `Started: ${fmtTime(start)}`]
            if (startAddr) lines.push(`Started Address: ${startAddr}`)
            if (stillMoving) {
              lines.push('Still moving')
            } else {
              lines.push(`Stopped: ${fmtTime(end)}`)
              if (endAddr) lines.push(`Stopped Address: ${endAddr}`)
            }
            lines.push(`Duration: ${formatDurationLong(start, end)}`)
            return lines.join('<br/>')
          },
        },
        encode: { x: [0, 1] },
        z: 4,
      })
    }

    // Rows that only ever show under the graph: the reefer detail fields and one row per geofence.
    const statusVisEnd = Math.min(Date.now(), periodWindow[1])
    allSeries.push(...buildReeferDetailSeries(data, placementFor, statusVisEnd, fmtTime))
    allSeries.push(...buildGeofenceRowSeries(data, hideGeofences, placementFor, statusVisEnd, fmtTime))

    // In-graph lanes: label each with its name in the left gutter (where the numeric
    // y-axis values used to be), vertically centered on the bar. A dedicated unclipped
    // series so the text can sit outside the plot area; the grid's left inset is widened
    // below to make room.
    if (hasAnyLane) {
      const laneLabels = statusLanes.map((lane) => ({ y: laneYByKey.get(lane.key) ?? 0, text: lane.label }))
      allSeries.push({
        type: 'custom',
        name: '__lane_labels__',
        xAxisIndex: 0,
        yAxisIndex: 0,
        clip: false,
        silent: true,
        z: 20,
        data: laneLabels.map((l) => ({ value: [periodWindow[0], l.y] })),
        renderItem: (
          params: { dataIndex: number; coordSys: { x: number } },
          api: { coord: (v: [number, number]) => [number, number]; value: (idx: number) => number }
        ) => {
          const centerY = api.coord([0, api.value(1)])[1]
          return {
            type: 'text',
            x: params.coordSys.x - 6,
            y: centerY,
            style: {
              text: laneLabels[params.dataIndex].text,
              textAlign: 'right',
              textVerticalAlign: 'middle',
              fontSize: 10,
              fill: '#374151',
              width: 120,
              overflow: 'truncate',
            },
          }
        },
      })
    }

    // Hidden legend: ECharts still needs legend data + selected for series visibility
    const tempLegendNames = temp.series.map((s) => s.name as string)
    const legendData = [
      ...tempLegendNames,
      ...citLegendNames,
      ...(powerSamples.length ? ['Power State'] : []),
      ...zoneModeLanes.map(([zoneName]) => `${zoneName} Mode`),
      ...(movementSamples.length ? ['Movement'] : []),
      ...(geofenceResult.markArea.data?.length ? ['__geofence__'] : []),
      ...(temperatureLimits?.upper != null ? [TEMP_LIMIT_UPPER_SERIES] : []),
      ...(temperatureLimits?.lower != null ? [TEMP_LIMIT_LOWER_SERIES] : []),
      ...(insideDoors.length ? ['__door_open_inside__'] : []),
      ...(outsideDoors.length ? ['__door_open_outside__'] : []),
      ...(plainDoors.length ? [DOOR_OPEN_SERIES] : []),
      ...(alertPins.length ? [ALERT_PINS_SERIES] : []),
    ]
    // Shared time-axis builder so both grids agree.
    const makeTimeAxis = (gridIndex: number, showLabel: boolean) =>
      makeSharedTimeAxis({ gridIndex, showLabel, periodWindow, zone })

    // Rows under the graph push the temperature grid up by their block; the container grows
    // by the same amount (AssetHistoryChart) so the temperature area keeps its size.
    const belowH = belowRows.length * STATUS_ROW_PX
    const gridLeft = hasAnyLane || hasBelow ? STATUS_LABEL_LEFT : GRID_SHARED.left

    const temperatureGrid = {
      ...GRID_SHARED,
      // Widen the left inset when status lanes or rows are present so their names fit in
      // the gutter (where the numeric y-values were) without crowding the axis name.
      left: gridLeft,
      // The alert marker renders a label above the plot top; reserve a fixed amount
      // of headroom when one is present so its (up to 2-line) tooltip isn't clipped
      // by the panel title/tabs. Other charts keep the default percentage top.
      top: hasTopMarkers ? 64 : showTabs ? '10%' : '3%',
      // Just enough for the 3-line axis label below the chart (no slider), plus the rows
      // under the graph when there are any.
      bottom: hasBelow ? STATUS_BELOW_PAD_PX + belowH + STATUS_GRID_GAP + STATUS_AXIS_PX : '16%',
      show: true,
      borderWidth: 1,
      borderColor: '#d1d5db',
    }

    const temperatureYAxis = {
      type: 'value',
      gridIndex: 0,
      min: yAxisMin,
      max: yAxisMax,
      name: tempUnitSuffix(units),
      nameLocation: 'middle',
      // Sit the unit name at the far left when the wider gutter holds lane names.
      nameGap: gridLeft === STATUS_LABEL_LEFT ? 100 : 40,
      axisLabel: {
        // The axis is padded below the data (for the status bars) and above it
        // (for CIT bars); blank out the tick labels that fall in those regions so
        // only real temperature values show against the bars.
        formatter: (value: number) => (value < tempMin - 0.001 || value > tempMax + 0.001 ? '' : `${value}`),
      },
      splitLine: {
        show: true,
        interval: 10,
        lineStyle: { color: '#d1d5db', opacity: 0.5 },
      },
    }

    const option: EChartsOption = {
      animation: false,
      // The grid under the graph has no border or grid lines: just the row names and bars.
      grid: hasBelow
        ? [
            temperatureGrid,
            { left: gridLeft, right: GRID_SHARED.right, height: belowH, bottom: STATUS_BELOW_PAD_PX, show: false },
          ]
        : [temperatureGrid],
      xAxis: hasBelow
        ? [makeTimeAxis(0, true), { ...makeTimeAxis(1, false), splitLine: { show: false } }]
        : [makeTimeAxis(0, true)],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      yAxis: (hasBelow ? [temperatureYAxis, buildStatusRowsAxis(belowRows)] : [temperatureYAxis]) as any,
      tooltip: {
        trigger: 'item',
        confine: true,
      },
      legend: {
        show: false,
        data: legendData,
        selected: mergedSelected,
      },
      dataZoom: [
        {
          type: 'inside',
          // One zoom spans both grids so the rows scroll and zoom with the graph.
          xAxisIndex: hasBelow ? [0, 1] : [0],
          filterMode: 'none',
          // Only allow narrowing within the period window
          minSpan: 0,
          maxSpan: 100,
          throttle: 30,
        },
      ],
      series: allSeries,
    }

    return option
  }, [
    data,
    tempResult,
    legendSelected,
    citState,
    citOverlays,
    periodWindow,
    units,
    zone,
    alertMarkers,
    temperatureLimits,
    showTabs,
    hideGeofences,
    statusLayout,
  ])
}
